/**
 * Soft≠Live Page161 — SA Team residual liquid (page79/126 buses + status).
 * Mount: /sa/team
 * Buses:
 *   GET /sa/team/status (always-on Live posture)
 *   GET /sa/team · POST /sa/team/provision · POST /sa/team/{u}/ttl
 *   POST /auth/users/{u}/sessions/revoke
 * Soft residual deleted:
 *  - sandpit roster without liquid rail / KPI / Re-read / status SoT
 *  - bare Refresh / Provision / Update TTL / End sessions CTAs without
 *  - Soft invent roster when GET fails or status list_allowed false (honest blank)
 *  - Soft provision success without Live 200 body
 *  - Soft invent create when SA_STAFF_PROVISION_ENABLED=0
 */
import { useMemo, useState } from "react";
import { html, F, Ico, Input, Field, Card, Alert, Ticker, go, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { Unavailable } from "./sa-shared.js";
import { formatUserInstant } from "./time.js";

/** Pure: root SA may provision; superadmin always; staff never invents. */
export function canProvisionStaffOf(me) {
  if (!me) return false;
  return String(me.role || "") === "superadmin";
}

/** Pure: read roster seat client guess — Live status wins when present. */
export function canReadStaffOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin" || r === "sa_staff") return true;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) => /sa_|full/i.test(String(c || "").trim()));
}

/** Pure: staff row from Live pack — never invent Soft TTL. */
export function staffRowOf(t) {
  if (!t || typeof t !== "object") return null;
  const username = String(t.username || "").trim();
  if (!username) return null;
  const untilRaw = t.sa_capabilities_granted_until != null ? t.sa_capabilities_granted_until : t.granted_until;
  const untilN = Number(untilRaw);
  const until = Number.isFinite(untilN) && untilN > 0 ? untilN : null;
  let caps = [];
  if (Array.isArray(t.capabilities) && t.capabilities.length) {
    caps = t.capabilities.map((c) => String(c || "").trim()).filter(Boolean);
  } else {
    const csv = t.sa_capabilities_csv != null ? t.sa_capabilities_csv : t.capabilities;
    caps = String(csv || "").split(/[,\s]+/).map((c) => c.trim()).filter(Boolean);
  }
  return {
    username,
    email: String(t.email || "").trim(),
    caps,
    until,
    ttlState: String(t.sa_capabilities_ttl_state || (until ? "active" : "no-ttl")).trim(),
    status: String(t.status || "").trim() || "—",
  };
}

/** Pure: Roster items. */
export function staffRegisterOf(pack) {
  const items = (pack && pack.items) || [];
  if (!Array.isArray(items)) return [];
  return items.map(staffRowOf).filter(Boolean);
}

/** Pure: KPI strip — blanks until Live. */
export function staffKpisOf(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const active = list.filter((r) => r && r.status === "active").length;
  const expired = list.filter((r) => r && (r.status === "expired" || r.ttlState === "expired")).length;
  const withTtl = list.filter((r) => r && r.until).length;
  return {
    accounts: list.length,
    active,
    expired,
    withTtl,
  };
}

function when(ts) {
  return !ts ? "—" : formatUserInstant(Number(ts), { dateOnly: true });
}
function soon(ts) {
  return ts && Number(ts) * 1000 - Date.now() < 7 * 864e5;
}

export function SaTeamDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(path("get__sa_team_status"));
  // Fail-closed while loading — Soft invent open seats from client role deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const listAllowed = post.list_allowed === true;
  const canProvision = post.provision_allowed === true;
  const ttlAllowed = post.ttl_allowed === true;
  const detail = post.detail || null;
  const productFlag = post.product_flag === true
    ? true
    : (post.product_flag === false ? false : null);
  const r = useApi(listAllowed ? path("get__sa_team") : "");
  const { run, busy, error, envelope } = useAction();
  const rows = useMemo(() => staffRegisterOf(r.data), [r.data]);
  const kpis = useMemo(() => staffKpisOf(rows), [rows]);
  const denied = !!(r.error && /\b40[13]\b|forbidden|insufficient|capability/i.test(String(r.error)));

  const [u, setU] = useState("");
  const [pw, setPw] = useState("");
  const [em, setEm] = useState("");
  const [capCsv, setCapCsv] = useState("sa_audit_read");
  const [untilLocal, setUntilLocal] = useState("");
  const [ttlUser, setTtlUser] = useState("");
  const [ttlUntil, setTtlUntil] = useState("");

  const parseUntil = (s) => {
    const t = String(s || "").trim();
    if (!t) return null;
    if (t.toLowerCase() === "clear" || t.toLowerCase() === "none") return null;
    const ms = Date.parse(t.includes("T") ? t : t.replace(" ", "T") + (t.endsWith("Z") ? "" : "Z"));
    if (!Number.isFinite(ms)) return NaN;
    return Math.floor(ms / 1000);
  };

  const reRead = () => {
    posture.reload && posture.reload();
    r.reload && r.reload();
    toast && toast("Team posture + roster re-read", "go");
  };

  const provision = async () => {
    if (!canProvision) {
      toast && toast("Only the root SA can provision staff.", "warn");
      return;
    }
    const caps = String(capCsv || "").split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!u.trim() || pw.length < 12 || !em.trim()) {
      toast && toast("Username, email and password (≥12) are required", "warn");
      return;
    }
    const body = { username: u.trim(), password: pw, email: em.trim(), capabilities: caps };
    if (untilLocal.trim()) {
      const gu = parseUntil(untilLocal);
      if (gu !== null && !Number.isFinite(gu)) {
        toast && toast("granted_until must be ISO datetime", "bad");
        return;
      }
      if (gu != null) body.granted_until = gu;
    }
    const out = await run(path("post__sa_team_provision"), { body });
    if (!out) {
      toast && toast(error || "Provision refused — nothing was created", "bad");
      return;
    }
    toast && toast("Staff provisioned · activation email queued", "go");
    setU(""); setPw(""); setEm("");
    r.reload && r.reload();
  };

  const setTtl = async () => {
    if (!ttlAllowed) {
      toast && toast("TTL write is closed on this seat.", "warn");
      return;
    }
    if (!ttlUser.trim()) { toast && toast("Username required", "warn"); return; }
    const raw = String(ttlUntil || "").trim();
    let body;
    if (!raw || raw.toLowerCase() === "clear" || raw.toLowerCase() === "none") {
      body = { granted_until: null };
    } else {
      const gu = parseUntil(raw);
      if (!Number.isFinite(gu)) {
        toast && toast("TTL until must be ISO datetime (or clear)", "bad");
        return;
      }
      body = { granted_until: gu };
    }
    const out = await run(path("post__sa_team_p_ttl", { pathParams: [ttlUser.trim()] }), { body });
    if (!out) {
      toast && toast(error || "TTL update refused — nothing was changed", "bad");
      return;
    }
    toast && toast("Staff TTL updated", "go");
    r.reload && r.reload();
  };

  const endSessions = async (name) => {
    const n = String(name || "").trim();
    if (!n) return;
    const out = await run(path("post__auth_users_p_sessions_revoke", { pathParams: [n] }));
    if (!out) {
      toast && toast("Could not end sessions refuse · " + n, "bad");
      return;
    }
    toast && toast("Sessions ended · " + n, "warn");
  };

  if (!listAllowed) {
    return html`<div className="sa-team-desk sa-team-desk--liquid"
      data-cmc-page="page126-sa-team" data-cmc-legacy="page79-sa-team"
      data-cmc-residual="page161-team">
      <div className="sa-team-desk__rail">
        <div>
          <p className="eyebrow">Platform · Staff</p>
          <h2 className="sa-team-desk__title">Seat closed</h2>
          <p className="note">Staff roster stays empty until this seat opens.</p>
        </div>
        <div className="btn-row sa-team-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/caps")}>Capabilities</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/audit")}>Audit</button>
        </div>
      </div>
      <${Card} title="Team posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            List and provision sealed
            · product ${productFlag === null ? "—" : productFlag ? "on" : "off"}
            · ${detail || "not available on this account"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Staff roster not on this account"
        say=${"Staff roster needs the root SA with staff provision armed. This account is "
          + (me && me.role ? me.role : "operator")
          + "."}/>
      <${Card} title="Delegated staff" meta="gated">
        <p className="note">No staff rows are offered on this grant.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-team-desk sa-team-desk--liquid"
    data-cmc-page="page126-sa-team" data-cmc-legacy="page79-sa-team"
    data-cmc-residual="page161-team">
    <div className="sa-team-desk__rail">
      <div>
        <p className="eyebrow">Platform · Staff</p>
        <h2 className="sa-team-desk__title">Delegated operators</h2>
        <p className="note">
          High-speed staff actions. Only the root SA provisions.
          Use TTL or disable to end a grant — bits are not invented locally.
        </p>
      </div>
      <div className="btn-row sa-team-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${r.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/caps")}>Capabilities</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/audit")}>Audit</button>
      </div>
    </div>

    <${Card} title="Team posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-team-desk__kpis" role="group" aria-label="Team posture">
          ${[
            ["Seat", listAllowed ? "open" : "gated", listAllowed ? "1" : "0", "status Live"],
            ["Product", productFlag ? "flag on" : "flag off", productFlag ? "1" : "0", "SA_STAFF_PROVISION"],
            ["Provision", canProvision ? "armed" : "—", canProvision ? "1" : "0", "POST provision"],
            ["TTL write", ttlAllowed ? "armed" : "—", ttlAllowed ? "1" : "0", "TTL"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-team-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          List and provision only after seats open
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Staff desk"
      say="Staff roster comes from the server when the list seat is open. Only the root SA provisions staff. Staff are delegated operators under that one SA, with parental capability subset and TTL."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${error ? html`<${Alert} tone="warn" title="Last staff write failed" say=${error}/>` : null}
    ${r.error ? html`
      <${Alert} tone=${denied ? "warn" : "bad"} title=${denied ? "Roster gated" : "Roster read failed"}
        say=${String(r.error)}/>` : null}

    <div className="sa-team-desk__kpis" role="group" aria-label="Staff KPIs">
      ${[
        ["Accounts", kpis.accounts, kpis.accounts ? "1" : "0", "Roster"],
        ["Active", kpis.active, kpis.active ? "1" : "0", "status Live"],
        ["Expired", kpis.expired, kpis.expired ? "1" : "0", "TTL/status Live"],
        ["With TTL", kpis.withTtl, kpis.withTtl ? "1" : "0", "wall-clock Live"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-team-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} title="Delegated staff" meta=${rows.length ? rows.length + " accounts" : "Empty or gated"}
        sub="Only the root account can provision staff, and it can only grant capabilities it already holds. Every grant can carry an expiry.">
      ${!rows.length ? html`<${Unavailable} r=${r} what="the staff roster"/>` : html`
      <div className="sa-team-desk__table-wrap">
        <table className="tbl">
          <thead><tr><th>Account</th><th>Capabilities</th><th>Expires</th><th>TTL</th><th>Status</th><th></th></tr></thead>
          <tbody>${rows.map((t) => html`
            <tr key=${t.username}>
              <td><b>${t.username}</b><div className="m">${t.email || ""}</div></td>
              <td><div className="chips">${t.caps
                    .map((c) => html`<span key=${c} className="chip" data-tone="ai">${c.replace("sa_", "")}</span>`)}</div></td>
              <td><span className="m" style=${soon(t.until) ? { color: "var(--amber)" } : null}>${when(t.until)}</span></td>
              <td><span className="chip" data-tone=${t.ttlState === "expired" ? "bad" : t.ttlState === "active" ? "" : "warn"}>${t.ttlState}</span></td>
              <td><span className="chip" data-tone=${t.status === "active" ? "" : t.status === "expired" ? "bad" : "warn"}>${t.status}</span></td>
              <td>
                <button className="btn btn--ghost btn--xs" type="button"
                  disabled=${!ttlAllowed}
                  onClick=${() => {
                    setTtlUser(t.username);
                    setTtlUntil(t.until
                      ? formatUserInstant(Number(t.until), { seconds: false })
                      : "");
                  }}>Edit TTL</button>
                <button className="btn btn--danger btn--xs" type="button" disabled=${busy}
                  onClick=${() => endSessions(t.username)}>End sessions</button>
              </td>
            </tr>`)}</tbody>
        </table>
      </div>`}
    <//>

    <div className="grid g2 sa-team-desk__grid">
      <${Card} title="Provision staff" meta="Provision"
          sub=${canProvision
            ? "Creates sa_staff, stores capability CSV under parental subset, queues activation OTP email."
            : "Root SA only — this principal cannot provision."}>
        ${!canProvision ? html`<p className="note">Provision controls stay hidden on non-root seats.</p>` : html`
          <${Field} label="Username"><${Input} icon="user" value=${u} onChange=${(e) => setU(e.target.value)} placeholder="staff.handle"/><//>
          <${Field} label="Email"><${Input} icon="mail" type="email" value=${em} onChange=${(e) => setEm(e.target.value)}/><//>
          <${Field} label="Password" hint="At least 12 characters.">
            <${Input} icon="key" type="password" value=${pw} onChange=${(e) => setPw(e.target.value)} autocomplete="new-password"/><//>
          <${Field} label="Capabilities (CSV)">
            <${Input} value=${capCsv} onChange=${(e) => setCapCsv(e.target.value)} placeholder="sa_audit_read,sa_device_admin"/><//>
          <${Field} label="Granted until (optional ISO)">
            <${Input} value=${untilLocal} onChange=${(e) => setUntilLocal(e.target.value)} placeholder="2026-12-31T23:59"/><//>
          <button className="btn btn--go btn--sm" disabled=${busy} onClick=${provision}>
            Provision</button>`}
      <//>
      <${Card} title="Set staff TTL" meta="TTL"
          sub="Bumps granted until. Type clear to remove a wall-clock expiry when the API allows null.">
        <${Field} label="Username"><${Input} icon="user" value=${ttlUser} onChange=${(e) => setTtlUser(e.target.value)} disabled=${!ttlAllowed}/><//>
        <${Field} label="Granted until (ISO or clear)">
          <${Input} value=${ttlUntil} onChange=${(e) => setTtlUntil(e.target.value)} placeholder="2026-12-31T23:59 or clear" disabled=${!ttlAllowed}/><//>
        <button className="btn btn--ghost btn--sm" disabled=${busy || !ttlAllowed || !ttlUser.trim()}
          onClick=${setTtl}>Update TTL</button>
      <//>
    </div>

    <${Card} title="How a grant ends" sub="Server levers only — instant revoke invent is not offered here.">
      ${[["TTL expires", "Capabilities drop on the next request after granted_until.", "warn"],
         ["Account disabled", "The whole account stops, capabilities included.", "bad"],
         ["No instant revoke", "Bits are cleared only by TTL or disable — Caps does not invent a silent strip.", "warn"]].map(([k, v, t]) => html`
        <div key=${k} className="verdict" data-ok="0">
          <span className="verdict__st" style=${t === "warn" ? { background: "rgba(255,182,92,.13)", color: "var(--amber)" } : null}>
            <${Ico} d="clock" w=${13}/></span>
          <span className="txt"><b style=${{ fontFamily: "var(--sans)", fontWeight: 500 }}>${k}</b><span>${v}</span></span>
        </div>`)}
    <//>
  </div>`;
}

export function Team({ toast }) {
  return html`<${F} data-cmc-page="page79-sa-team-mount">
    <${SaTeamDesk} toast=${toast}/>
  <//>`;
}
